import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../logo.png';

const productLinks = [
  { label: 'HealHub', href: '/healhub', desc: 'Clinic Management System' },
  { label: 'Suraksha', href: '/suraksha', desc: 'AI Anti-Fraud Protection' },
  { label: 'Vedant', href: '/vedant', desc: 'AI Health Assistant' },
  { label: 'HealHub Nexus', href: '/healhub', desc: 'Enterprise Platform' },
];

const hireLinks = [
  {
    category: 'Backend',
    skills: ['Laravel PHP', 'Django Python', 'Node.js', 'Java Spring', 'MEAN Stack', 'MERN Stack'],
  },
  {
    category: 'Frontend',
    skills: ['React.js', 'Vue.js', 'Angular', 'Next.js'],
  },
  {
    category: 'Mobile',
    skills: ['React Native', 'Flutter', 'iOS Swift', 'Android Kotlin'],
  },
  {
    category: 'UX / Design',
    skills: ['Figma', 'UI Design', 'UX Research'],
  },
  {
    category: 'AI & Data',
    skills: ['Machine Learning', 'LLM Integration', 'Data Engineering'],
  },
  {
    category: 'Infrastructure',
    skills: ['DevOps', 'AWS', 'Firebase', 'Docker'],
  },
];

const mainLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Careers', href: '/careers' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const location = useLocation();

  const productsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hireTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setProductsOpen(false);
    setHireOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleProductsEnter = () => {
    if (productsTimer.current) clearTimeout(productsTimer.current);
    setProductsOpen(true);
    setHireOpen(false);
    if (hireTimer.current) clearTimeout(hireTimer.current);
  };

  const handleProductsLeave = () => {
    productsTimer.current = setTimeout(() => setProductsOpen(false), 150);
  };

  const handleHireEnter = () => {
    if (hireTimer.current) clearTimeout(hireTimer.current);
    setHireOpen(true);
    setProductsOpen(false);
    if (productsTimer.current) clearTimeout(productsTimer.current);
  };

  const handleHireLeave = () => {
    hireTimer.current = setTimeout(() => setHireOpen(false), 150);
  };

  const isProductActive = ['/healhub', '/suraksha', '/vedant', '/products'].includes(
    location.pathname
  );

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          transition: 'all 0.4s ease',
          backgroundColor: scrolled ? 'rgba(5,8,16,0.95)' : 'rgba(5,8,16,0.6)',
          backdropFilter: 'blur(24px)',
          borderBottom: scrolled
            ? '1px solid rgba(59,130,246,0.15)'
            : '1px solid rgba(59,130,246,0.08)',
        }}
      >
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 32px',
            height: '72px',
            display: 'flex',
            alignItems: 'center',
            gap: '0',
          }}
        >
          {/* ── Logo ── */}
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flexShrink: 0,
              marginRight: '36px',
            }}
          >
            <img
              src={logo}
              alt="Redcubical"
              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
            />
          </Link>

          {/* ── Desktop Nav Links ── */}
          <div
            className="desktop-nav"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '28px',
              flex: 1,
            }}
          >
            {/* Products Dropdown */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={handleProductsEnter}
              onMouseLeave={handleProductsLeave}
            >
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isProductActive ? '#fff' : 'rgba(255,255,255,0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 0',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'color 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                Products
                <span
                  style={{
                    fontSize: '9px',
                    transform: productsOpen ? 'rotate(180deg)' : 'none',
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                  }}
                >
                  ▼
                </span>
              </button>

              {productsOpen && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '100%',
                      height: '12px',
                      background: 'transparent',
                      zIndex: 199,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      left: '-24px',
                      background: 'rgba(8,12,24,0.98)',
                      border: '1px solid rgba(59,130,246,0.2)',
                      borderRadius: '16px',
                      padding: '8px',
                      minWidth: '260px',
                      boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
                      backdropFilter: 'blur(20px)',
                      zIndex: 200,
                      animation: 'dropdownFadeIn 0.15s ease-out',
                    }}
                  >
                    {productLinks.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          textDecoration: 'none',
                          transition: 'background 0.2s',
                          gap: '2px',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = 'rgba(59,130,246,0.12)')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = 'transparent')
                        }
                      >
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>
                          {item.label}
                        </span>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.38)' }}>
                          {item.desc}
                        </span>
                      </Link>
                    ))}
                    <div
                      style={{
                        height: '1px',
                        background: 'rgba(59,130,246,0.15)',
                        margin: '6px 8px',
                      }}
                    />
                    <Link
                      to="/products"
                      style={{
                        display: 'block',
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#60A5FA',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(59,130,246,0.1)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      View All Products →
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Hire Developers Dropdown */}
            <div
              style={{ position: 'relative' }}
              onMouseEnter={handleHireEnter}
              onMouseLeave={handleHireLeave}
            >
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color:
                    location.pathname === '/hire-developers'
                      ? '#fff'
                      : 'rgba(255,255,255,0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '8px 0',
                  fontFamily: 'Inter, sans-serif',
                  transition: 'color 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                Hire Developers
                <span
                  style={{
                    fontSize: '9px',
                    transform: hireOpen ? 'rotate(180deg)' : 'none',
                    display: 'inline-block',
                    transition: 'transform 0.2s',
                  }}
                >
                  ▼
                </span>
              </button>

              {hireOpen && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '100%',
                      height: '12px',
                      background: 'transparent',
                      zIndex: 199,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      left: '-40px',
                      background: 'rgba(8,12,24,0.98)',
                      border: '1px solid rgba(59,130,246,0.2)',
                      borderRadius: '16px',
                      padding: '24px',
                      width: '660px',
                      boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
                      backdropFilter: 'blur(20px)',
                      zIndex: 200,
                      animation: 'dropdownFadeIn 0.15s ease-out',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '24px',
                      }}
                    >
                      {hireLinks.map((group) => (
                        <div key={group.category}>
                          <p
                            style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              letterSpacing: '2px',
                              color: '#3B82F6',
                              textTransform: 'uppercase',
                              marginBottom: '12px',
                            }}
                          >
                            {group.category}
                          </p>
                          <div
                            style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                          >
                            {group.skills.map((skill) => (
                              <Link
                                key={skill}
                                to="/services#augmentation"
                                style={{
                                  fontSize: '13px',
                                  color: 'rgba(255,255,255,0.6)',
                                  textDecoration: 'none',
                                  transition: 'color 0.2s',
                                  padding: '4px 0',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = '#fff')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')
                                }
                              >
                                {skill}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        height: '1px',
                        background: 'rgba(59,130,246,0.15)',
                        margin: '20px 0 16px',
                      }}
                    />
                    <Link
                      to="/services#augmentation"
                      style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#60A5FA',
                        textDecoration: 'none',
                      }}
                    >
                      View All Developer Profiles →
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Main Links */}
            {mainLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color:
                    location.pathname === link.href ? '#fff' : 'rgba(255,255,255,0.65)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  padding: '8px 0',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color =
                    location.pathname === link.href ? '#fff' : 'rgba(255,255,255,0.65)')
                }
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* ── Right side: Trust badge + CTA + Hamburger ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexShrink: 0,
              marginLeft: '24px',
            }}
          >
            {/* Trust badge — fills the gap, desktop only */}
            <div
              className="desktop-trust"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.18)',
                borderRadius: '50px',
                padding: '6px 14px',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: '13px' }}>🇮🇳</span>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.55)',
                  letterSpacing: '0.3px',
                }}
              >
                Built in India
              </span>
              <span
                style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: '#22C55E',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#22C55E',
                }}
              >
                3 Products Live
              </span>
            </div>

            {/* Desktop CTA */}
            <Link
              to="/contact"
              className="desktop-cta"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 22px',
                borderRadius: '50px',
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '700',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'opacity 0.2s, transform 0.2s',
                boxShadow: '0 4px 20px rgba(59,130,246,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Get In Touch ↗
            </Link>

            {/* Hamburger */}
            <button
              className="hamburger-btn"
              onClick={() => setMenuOpen((prev) => !prev)}
              style={{
                display: 'none',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '5px',
                width: '40px',
                height: '40px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                cursor: 'pointer',
                padding: '4px',
                flexShrink: 0,
              }}
              aria-label="Toggle menu"
            >
              <span
                style={{
                  display: 'block',
                  width: '18px',
                  height: '2px',
                  background: '#fff',
                  borderRadius: '2px',
                  transition: 'transform 0.3s, opacity 0.3s',
                  transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  width: '18px',
                  height: '2px',
                  background: '#fff',
                  borderRadius: '2px',
                  transition: 'opacity 0.3s',
                  opacity: menuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  width: '18px',
                  height: '2px',
                  background: '#fff',
                  borderRadius: '2px',
                  transition: 'transform 0.3s, opacity 0.3s',
                  transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
                }}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile Menu ── */}
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            background: 'rgba(5,8,16,0.99)',
            backdropFilter: 'blur(20px)',
            overflowY: 'auto',
          }}
        >
          {/* Mobile menu header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 24px',
              height: '72px',
              borderBottom: '1px solid rgba(59,130,246,0.12)',
            }}
          >
            <Link
              to="/"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <img
                src={logo}
                alt="Redcubical"
                style={{ width: '36px', height: '36px', objectFit: 'contain' }}
              />
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '20px',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>
          </div>

          {/* Mobile menu body */}
          <div style={{ padding: '32px 24px 48px' }}>
            {/* Products section */}
            <p
              style={{
                fontSize: '10px',
                fontWeight: '700',
                letterSpacing: '3px',
                color: '#3B82F6',
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              Products
            </p>
            {productLinks.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                style={{
                  fontSize: '22px',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: '900',
                  color: '#fff',
                  textDecoration: 'none',
                  padding: '14px 0',
                  textTransform: 'uppercase',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>
                  {item.label}
                  <span
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '400',
                      color: 'rgba(255,255,255,0.35)',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {item.desc}
                  </span>
                </span>
                <span style={{ color: '#3B82F6', fontSize: '20px' }}>→</span>
              </Link>
            ))}

            <div
              style={{
                height: '1px',
                background: 'rgba(59,130,246,0.2)',
                margin: '24px 0',
              }}
            />

            {/* Hire Developers */}
            <p
              style={{
                fontSize: '10px',
                fontWeight: '700',
                letterSpacing: '3px',
                color: '#3B82F6',
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              Hire
            </p>
            <Link
              to="/services#augmentation"
              style={{
                fontSize: '22px',
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: '900',
                color: '#fff',
                textDecoration: 'none',
                padding: '14px 0',
                textTransform: 'uppercase',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              Hire Developers
              <span style={{ color: '#3B82F6', fontSize: '20px' }}>→</span>
            </Link>

            <div
              style={{
                height: '1px',
                background: 'rgba(59,130,246,0.2)',
                margin: '24px 0',
              }}
            />

            {/* Main links */}
            <p
              style={{
                fontSize: '10px',
                fontWeight: '700',
                letterSpacing: '3px',
                color: 'rgba(255,255,255,0.3)',
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              Navigate
            </p>
            {mainLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                style={{
                  fontSize: '22px',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: '900',
                  color: location.pathname === link.href ? '#60A5FA' : '#fff',
                  textDecoration: 'none',
                  padding: '14px 0',
                  textTransform: 'uppercase',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {link.label}
                <span
                  style={{
                    color: location.pathname === link.href ? '#60A5FA' : '#3B82F6',
                    fontSize: '20px',
                  }}
                >
                  →
                </span>
              </Link>
            ))}

            {/* Mobile CTA */}
            <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/contact"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '16px 28px',
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '700',
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(59,130,246,0.35)',
                }}
              >
                Get In Touch ↗
              </Link>
              <Link
                to="/products"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '16px 28px',
                  borderRadius: '50px',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '15px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                Explore Products →
              </Link>
            </div>

            {/* Footer note */}
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.2)',
                textAlign: 'center',
                marginTop: '40px',
                letterSpacing: '0.5px',
              }}
            >
              redcubicalsystems.com · Built in India 🇮🇳
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }
          .desktop-cta {
            display: none !important;
          }
          .desktop-trust {
            display: none !important;
          }
          .hamburger-btn {
            display: flex !important;
          }
        }

        @media (min-width: 1025px) and (max-width: 1200px) {
          .desktop-trust {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}